/**
 * The toolbar's tab and filter handlers.
 *
 * React-free and separated from `App` for the reason `viewPrefs.ts` and `route.ts` are:
 * this is the branchy part, and the bug it exists to prevent lives in the WIRING rather
 * than in any predicate — so it has to be testable without a DOM.
 */

import { BOARD, isAwayFromBoard, type Route } from './route';
import { TAB, tabShowsTickets } from './viewPrefs';

export interface BoardControlsInput {
  /** The page underneath — the board, or an epic. */
  base: Route;
  tab: number;
  /**
   * ⚠️ Must be a NO-OP when the route asked for is already current.
   *
   * THE reason, stated here once because three modules depend on it: MUI fires `onClick`
   * AND `onChange` for one real interaction, so every handler below runs twice with
   * `base` still stale between them and asks to navigate twice. An implementation that
   * pushed both would leave a duplicate history entry and Back would stop working in one
   * press. `useAppRoute.navigate` satisfies this.
   *
   * The same double-fire is why the handlers are idempotent, and why the UI binds them to
   * click as well as change — MUI reports no change when you pick what is already picked.
   */
  navigate: (route: Route) => void;
  setTab: (next: number) => void;
  setEpic: (next: string) => void;
  setStatus: (next: string) => void;
}

/**
 * N7 — the names say that these navigate, because the plain `setTab`/`setFilters` they
 * wrap do not: choosing is the action, and where you end up is part of it.
 */
export interface BoardControls {
  chooseTab: (next: number) => void;
  chooseFilters: (next: { epic: string; status: string }) => void;
}

/**
 * Wires each control so that choosing it lands somewhere the choice is visible.
 *
 * There are two ways to be invisible and both have bitten: the control's effect can be
 * hidden behind a *page* (an epic page renders instead of the board), or behind a *tab*
 * that ignores it (the Epics tab reads neither filter). Leaving either one silently is
 * the failure DWLF-143 is about — see `isAwayFromBoard` for why it matters.
 */
export function makeBoardControls({
  base,
  tab,
  navigate,
  setTab,
  setEpic,
  setStatus,
}: BoardControlsInput): BoardControls {
  const showBoard = () => {
    if (isAwayFromBoard(base)) navigate(BOARD);
  };

  // Both are idempotent, which is what lets the UI call them on every click rather than
  // only on a change — MUI fires no change event when you pick what is already picked,
  // and "click the tab I am already on to get back to the board" is the commonest move
  // there is.
  // The double-call that follows from that is safe only because `navigate` is required to
  // be idempotent — see its field above.
  return {
    chooseTab: (next) => {
      setTab(next);
      showBoard();
    },
    chooseFilters: (next) => {
      setEpic(next.epic);
      setStatus(next.status);
      showBoard();
      // A filter is a statement about tickets, so land on a tab that shows them.
      if (!tabShowsTickets(tab)) setTab(TAB.board);
    },
  };
}
